import React, { useEffect, useState } from "react";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Skeleton } from "primereact/skeleton";
import { Dropdown } from "primereact/dropdown";
import { Paginator, PaginatorCurrentPageReportOptions, PaginatorRowsPerPageDropdownOptions } from "primereact/paginator";
import { StarIcon } from "@heroicons/react/24/solid";
import DropdownComponent from "../../components/form-control/drop-down";
import apiFunctions from "../../services/api-service";
import useToast from "../../components/common/useToast";

type Review = {
    reviewId: string;
    authorName: string;
    starRating: number;
    text: string;
    languageCode: string;
    device: string;
    appVersionName: string;
    thumbsUpCount: number;
    reviewCreatedAt: string;
    developerReply: string;
    developerReplyAt: string;
    canReply?: boolean;
};

const RATING_OPTIONS = [
    { name: "All Ratings", id: "" },
    { name: "5 Stars", id: "5" },
    { name: "4 Stars", id: "4" },
    { name: "3 Stars", id: "3" },
    { name: "2 Stars", id: "2" },
    { name: "1 Star", id: "1" },
];

const REPLIED_OPTIONS = [
    { name: "All", id: "" },
    { name: "Replied", id: "true" },
    { name: "Not Replied", id: "false" },
];

const COMMENTED_OPTIONS = [
    { name: "All", id: "" },
    { name: "Commented", id: "true" },
    { name: "Non Commented", id: "false" },
];

const StarRow: React.FC<{ rating: number }> = ({ rating }) => (
    <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((i) => (
            <StarIcon key={i} style={{ width: 16, height: 16, color: i <= rating ? "#f59e0b" : "#e5e7eb" }} />
        ))}
    </div>
);

const formatDate = (d: string) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");

const PlayReviewsPage: React.FC = () => {
    const { showToast } = useToast();
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [reviews, setReviews] = useState<Review[]>([]);
    const [total, setTotal] = useState(0);
    const [stats, setStats] = useState<{ averageRating: number | null; totalReviews: number | null; installs: string | null }>({ averageRating: null, totalReviews: null, installs: null });
    const [currentPage, setCurrentPage] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState<any>(10);
    const [ratingFilter, setRatingFilter] = useState("");
    const [repliedFilter, setRepliedFilter] = useState("");
    const [commentedFilter, setCommentedFilter] = useState("");

    const [replyingTo, setReplyingTo] = useState<Review | null>(null);
    const [replyText, setReplyText] = useState("");
    const [posting, setPosting] = useState(false);

    const fetchReviews = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ page: String(currentPage + 1), limit: String(itemsPerPage) });
            if (ratingFilter) params.set("rating", ratingFilter);
            if (repliedFilter) params.set("replied", repliedFilter);
            if (commentedFilter) params.set("commented", commentedFilter);
            const res = await apiFunctions.get(`/playreviews?${params.toString()}`);
            if (res?.data?.success) {
                setReviews(res.data.data.reviews || []);
                setTotal(res.data.data.total || 0);
                setStats(res.data.data.stats || { averageRating: null, totalReviews: null, installs: null });
            } else {
                showToast({ severity: "error", summary: "Error", detail: res?.data?.message || "Failed to load reviews", life: 3000 });
            }
        } catch (err) {
            console.error("Failed to load Play Store reviews:", err);
            showToast({ severity: "error", summary: "Error", detail: "Failed to load reviews", life: 3000 });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReviews();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, itemsPerPage, ratingFilter, repliedFilter, commentedFilter]);

    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
    };

    const handleItemsPerPageChange = (value: number) => {
        setItemsPerPage(value);
        setCurrentPage(0);
    };

    const handleSync = async () => {
        setSyncing(true);
        try {
            const res = await apiFunctions.post("/playreviews/sync", {});
            if (res?.data?.success) {
                showToast({ severity: "success", summary: "Synced", detail: `${res.data.data.synced} reviews synced from Google Play`, life: 3000 });
                fetchReviews();
            } else {
                showToast({ severity: "error", summary: "Error", detail: res?.data?.message || "Sync failed", life: 3000 });
            }
        } catch (err) {
            console.error("Sync failed:", err);
            showToast({ severity: "error", summary: "Error", detail: "Sync failed", life: 3000 });
        } finally {
            setSyncing(false);
        }
    };

    const openReply = (review: Review) => {
        setReplyingTo(review);
        setReplyText(review.developerReply || "");
    };

    const submitReply = async () => {
        if (!replyingTo || !replyText.trim()) return;
        setPosting(true);
        try {
            const res = await apiFunctions.post(`/playreviews/${replyingTo.reviewId}/reply`, { reply: replyText.trim() });
            if (res?.data?.success) {
                showToast({ severity: "success", summary: "Posted", detail: "Reply posted to Google Play", life: 3000 });
                setReplyingTo(null);
                setReplyText("");
                fetchReviews();
            } else {
                showToast({ severity: "error", summary: "Error", detail: res?.data?.message || "Failed to post reply", life: 3000 });
            }
        } catch (err) {
            console.error("Reply failed:", err);
            showToast({ severity: "error", summary: "Error", detail: "Failed to post reply", life: 3000 });
        } finally {
            setPosting(false);
        }
    };

    const totalPages = Math.max(1, Math.ceil(total / itemsPerPage));

    const paginatoreTemplate = {
        layout: "RowsPerPageDropdown CurrentPageReport",
        RowsPerPageDropdown: (options: PaginatorRowsPerPageDropdownOptions) => {
            const dropdownOptions = [
                { label: 10, value: 10 },
                { label: 25, value: 25 },
                { label: 50, value: 50 },
                { label: 100, value: 100 },
                { label: 200, value: 200 },
            ];

            return (
                <div className="paginator-dropdown">
                    <span className="mx-1">Show</span>
                    <Dropdown
                        value={itemsPerPage}
                        options={dropdownOptions}
                        onChange={(e) => handleItemsPerPageChange(e.value)}
                    />
                </div>
            );
        },
        CurrentPageReport: (options: PaginatorCurrentPageReportOptions) => {
            return <span className="entries-text mx-1">entries</span>;
        },
    };

    return (
        <div className="dashboard-margin">
            <div className="grid grid-cols-12 gap-4 mt-4 items-center">
                <div className="col-span-12 md:col-span-6">
                    <h2 className="card-title m-0">Play Store Reviews</h2>
                    <p className="text-xs m-0 mt-1" style={{ color: "#000000" }}>Live from Google Play — replies post directly to the Play Store</p>
                </div>
                <div className="col-span-12 md:col-span-6 flex justify-start md:justify-end items-center gap-3 flex-wrap">
                    <div className="w-full md:w-40">
                        <DropdownComponent id="rating" placeholder="Rating" modelValue={ratingFilter} options={RATING_OPTIONS} onUpdateModelValue={(v: string) => { setCurrentPage(0); setRatingFilter(v); }} showClear />
                    </div>
                    <div className="w-full md:w-40">
                        <DropdownComponent id="replied" placeholder="Reply Status" modelValue={repliedFilter} options={REPLIED_OPTIONS} onUpdateModelValue={(v: string) => { setCurrentPage(0); setRepliedFilter(v); }} showClear />
                    </div>
                    <div className="w-full md:w-40">
                        <DropdownComponent id="commented" placeholder="Comment" modelValue={commentedFilter} options={COMMENTED_OPTIONS} onUpdateModelValue={(v: string) => { setCurrentPage(0); setCommentedFilter(v); }} showClear />
                    </div>
                    <Button label={syncing ? "Syncing..." : "Sync Now"} icon={<span className="mr-1">↻</span>} disabled={syncing} onClick={handleSync} style={{ background: "#264164", border: "none" }} />
                </div>
            </div>

            {/* Total average rating / review count / installs — mirrors the Play
                Store listing's own public numbers, not just what's synced locally. */}
            <div className="grid grid-cols-12 gap-4 mt-4">
                <div className="col-span-12 sm:col-span-4">
                    <div className="card h-full">
                        <Card title="Average Rating">
                            {loading ? (
                                <Skeleton height="1.75rem" width="60%" />
                            ) : (
                                <div className="flex items-center gap-2">
                                    <span style={{ fontSize: "22px", fontWeight: 700, color: "#264164" }}>
                                        {stats.averageRating != null ? stats.averageRating.toFixed(1) : "—"}
                                    </span>
                                    {stats.averageRating != null && <StarRow rating={Math.round(stats.averageRating)} />}
                                </div>
                            )}
                        </Card>
                    </div>
                </div>
                <div className="col-span-12 sm:col-span-4">
                    <div className="card h-full">
                        <Card title="Total Reviews">
                            {loading ? (
                                <Skeleton height="1.75rem" width="40%" />
                            ) : (
                                <span style={{ fontSize: "22px", fontWeight: 700, color: "#264164" }}>
                                    {stats.totalReviews != null ? stats.totalReviews.toLocaleString() : "—"}
                                </span>
                            )}
                        </Card>
                    </div>
                </div>
                <div className="col-span-12 sm:col-span-4">
                    <div className="card h-full">
                        <Card title="Total Installations">
                            {loading ? (
                                <Skeleton height="1.75rem" width="40%" />
                            ) : (
                                <span style={{ fontSize: "22px", fontWeight: 700, color: "#264164" }}>
                                    {stats.installs || "—"}
                                </span>
                            )}
                        </Card>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-12 gap-4 mt-4">
                <div className="col-span-12">
                    {loading ? (
                        <div className="flex flex-col gap-3">
                            {Array.from({ length: 4 }).map((_, i) => (
                                <div className="card" key={i}><Card><Skeleton height="80px" /></Card></div>
                            ))}
                        </div>
                    ) : reviews.length === 0 ? (
                        <div className="card">
                            <Card>
                                <p className="text-center m-0" style={{ color: "#9ca3af" }}>
                                    No reviews yet — click "Sync Now" to pull the latest from Google Play.
                                </p>
                            </Card>
                        </div>
                    ) : (
                        <div className="flex flex-col gap-3">
                            {reviews.map((r) => (
                                <div className="card" key={r.reviewId}>
                                    <Card>
                                        <div className="flex justify-between items-start flex-wrap gap-2">
                                            <div>
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="font-semibold" style={{ color: "#264164" }}>{r.authorName || "Anonymous"}</span>
                                                    <StarRow rating={r.starRating} />
                                                </div>
                                                <p className="m-0 text-sm" style={{ color: "#374151" }}>{r.text}</p>
                                                <p className="m-0 mt-2 text-xs" style={{ color: "#9ca3af" }}>
                                                    {r.appVersionName && `v${r.appVersionName} · `}{r.device && `${r.device} · `}{formatDate(r.reviewCreatedAt)}
                                                </p>
                                            </div>
                                            {r.canReply === false ? (
                                                <span className="text-xs" style={{ color: "#9ca3af" }} title="Imported from historical export — Google has no reply-capable ID for this review">
                                                    Reply unavailable
                                                </span>
                                            ) : (
                                                <Button
                                                    label={r.developerReply ? "Edit Reply" : "Reply"}
                                                    text
                                                    onClick={() => openReply(r)}
                                                    style={{ color: "#264164" }}
                                                />
                                            )}
                                        </div>
                                        {r.developerReply && (
                                            <div className="mt-3 p-3 rounded-md" style={{ background: "#f3f4f6" }}>
                                                <p className="m-0 text-xs font-semibold" style={{ color: "#264164" }}>Developer Reply</p>
                                                <p className="m-0 mt-1 text-sm" style={{ color: "#374151" }}>{r.developerReply}</p>
                                                <p className="m-0 mt-1 text-xs" style={{ color: "#9ca3af" }}>{formatDate(r.developerReplyAt)}</p>
                                            </div>
                                        )}
                                    </Card>
                                </div>
                            ))}
                        </div>
                    )}

                    {!loading && total > 0 && (
                        <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-2 bg-white p-2 mt-2" style={{ borderRadius: "8px" }}>
                            <Paginator
                                template={paginatoreTemplate}
                                first={currentPage * itemsPerPage}
                                rows={itemsPerPage}
                                totalRecords={total}
                            />

                            <div className="flex items-center gap-2 w-full sm:w-auto">
                                <Button
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    className="pagination-button flex-shrink-0 px-2 sm:px-4"
                                    disabled={currentPage === 0}
                                >
                                    <span className="sm:hidden">Prev</span>
                                    <span className="hidden sm:inline">Previous</span>
                                </Button>

                                <Button className="pagination-button flex-1 min-w-0 cursor-default px-1 sm:px-4">
                                    <span className="pagination-text whitespace-nowrap text-xs sm:text-sm">
                                        Page {currentPage + 1} of {totalPages || 1}
                                    </span>
                                </Button>

                                <Button
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    className="pagination-button flex-shrink-0 px-2 sm:px-4"
                                    disabled={currentPage + 1 === (totalPages === 0 ? 1 : totalPages)}
                                >
                                    Next
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <Dialog
                header={replyingTo?.developerReply ? "Edit Reply" : "Reply to Review"}
                visible={!!replyingTo}
                style={{ width: "92vw", maxWidth: "480px" }}
                modal
                blockScroll
                onHide={() => setReplyingTo(null)}
            >
                {replyingTo && (
                    <div className="flex flex-col gap-3">
                        <div className="p-3 rounded-md" style={{ background: "#f9fafb" }}>
                            <div className="flex items-center gap-2 mb-1">
                                <span className="font-semibold text-sm">{replyingTo.authorName || "Anonymous"}</span>
                                <StarRow rating={replyingTo.starRating} />
                            </div>
                            <p className="m-0 text-sm" style={{ color: "#374151" }}>{replyingTo.text}</p>
                        </div>
                        <textarea
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value.slice(0, 350))}
                            maxLength={350}
                            rows={5}
                            placeholder="Write your reply..."
                            style={{ borderRadius: "6px", border: "1px solid #d1d5db", padding: "8px 10px", fontSize: "14px", resize: "vertical" }}
                        />
                        <div className="flex justify-between items-center">
                            <span className="text-xs" style={{ color: "#9ca3af" }}>{replyText.length}/350</span>
                            <Button
                                label={posting ? "Posting..." : replyingTo.developerReply ? "Update Reply" : "Post Reply"}
                                disabled={!replyText.trim() || posting}
                                onClick={submitReply}
                                style={{ background: "#264164", border: "none" }}
                            />
                        </div>
                    </div>
                )}
            </Dialog>
        </div>
    );
};

export default PlayReviewsPage;
